import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiLock,
  FiEye,
  FiEyeOff,
  FiCheckCircle,
  FiArrowLeft,
  FiRefreshCw,
  FiCopy,
} from "react-icons/fi";
import { authAPI } from "../../services/api";

const ResetPasswordContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem 1rem;
`;

const ResetPasswordCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  width: 100%;
  max-width: 500px;
`;

const ResetPasswordHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 0.875rem;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-left: 2.5rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
    box-shadow: 0 0 0 3px rgba(45, 80, 22, 0.1);
  }

  &.error {
    border-color: #dc3545;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 1.125rem;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 1.125rem;
  padding: 0.25rem;

  &:hover {
    color: #333;
  }
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
`;

const PasswordSuggestion = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 0.5rem;
`;

const SuggestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const SuggestionTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #2d5016;
`;

const SuggestionActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SuggestionButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;

  &:hover {
    background: #e9ecef;
    color: #2d5016;
  }
`;

const SuggestedPassword = styled.div`
  font-family: "Courier New", monospace;
  font-size: 0.875rem;
  color: #333;
  background: white;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  word-break: break-all;
  margin-bottom: 0.5rem;
`;

const PasswordStrength = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
`;

const StrengthBar = styled.div`
  height: 4px;
  border-radius: 2px;
  flex: 1;
  background: #e0e0e0;
  transition: background-color 0.3s ease;

  &.weak {
    background: #dc3545;
  }
  &.fair {
    background: #ffc107;
  }
  &.good {
    background: #17a2b8;
  }
  &.strong {
    background: #28a745;
  }
`;

const StrengthText = styled.span`
  font-size: 0.75rem;
  color: #666;
`;

const SubmitButton = styled.button`
  background: #2d5016;
  color: white;
  border: none;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: #1a3a0e;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BackToLogin = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e0e0e0;

  p {
    color: #666;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }

  a {
    color: #2d5016;
    text-decoration: none;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [suggestedPassword, setSuggestedPassword] = useState("");
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);
  const [error, setError] = useState("");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch("password");

  // Password generation function (same as Register.js)
  const generatePassword = () => {
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*()_+-=[]{}|;:,.<>?";

    let password = "";

    // Ensure at least one character from each category
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];

    // Fill the rest randomly
    const allChars = lowercase + uppercase + numbers + symbols;
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle the password
    return password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
  };

  // Password strength calculation
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { strength: 0, label: "", bars: 0 };

    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[a-z]/.test(pwd)) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;

    if (score <= 2)
      return { strength: score, label: "Weak", bars: 1, class: "weak" };
    if (score <= 3)
      return { strength: score, label: "Fair", bars: 2, class: "fair" };
    if (score <= 4)
      return { strength: score, label: "Good", bars: 3, class: "good" };
    return { strength: score, label: "Strong", bars: 4, class: "strong" };
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setSuggestedPassword(newPassword);
    setShowSuggestion(true);
  };

  const handleUseSuggestedPassword = () => {
    // Set the suggested password in the form
    const passwordInput = document.getElementById("password");
    const confirmPasswordInput = document.getElementById("confirmPassword");

    if (passwordInput) passwordInput.value = suggestedPassword;
    if (confirmPasswordInput) confirmPasswordInput.value = suggestedPassword;

    // Trigger form validation
    passwordInput?.dispatchEvent(new Event("input", { bubbles: true }));
    confirmPasswordInput?.dispatchEvent(new Event("input", { bubbles: true }));

    setShowSuggestion(false);
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(suggestedPassword);
      // You could add a toast notification here
    } catch (err) {
      console.error("Failed to copy password:", err);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError("");

    console.log("Reset password attempt:", { token, password: data.password });

    try {
      const response = await authAPI.resetPassword(token, data.password);

      if (response.data.success) {
        setPasswordReset(true);
      } else {
        setError(response.data.message || "Failed to reset password");
      }
    } catch (err) {
      console.error("Reset password error:", err);
      console.error("Error response:", err.response?.data);
      setError(
        err.response?.data?.message ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/forgot-password");
    }
  }, [token, navigate]);

  if (passwordReset) {
    return (
      <ResetPasswordContainer>
        <ResetPasswordCard
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ResetPasswordHeader>
            <h1>Password Reset Successfully!</h1>
            <p>
              Your password has been updated successfully. You can now sign in
              with your new password.
            </p>
          </ResetPasswordHeader>

          <SuccessMessage>
            <FiCheckCircle />
            Password reset completed successfully!
          </SuccessMessage>

          <BackToLogin>
            <Link to="/login">
              <FiArrowLeft />
              Back to Sign In
            </Link>
          </BackToLogin>
        </ResetPasswordCard>
      </ResetPasswordContainer>
    );
  }

  return (
    <ResetPasswordContainer>
      <ResetPasswordCard
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ResetPasswordHeader>
          <h1>Reset Your Password</h1>
          <p>
            Enter your new password below. Make sure it's strong and secure.
          </p>
        </ResetPasswordHeader>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="password">New Password</Label>
            <InputContainer>
              <InputIcon>
                <FiLock />
              </InputIcon>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your new password"
                className={errors.password ? "error" : ""}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </PasswordToggle>
            </InputContainer>
            {errors.password && (
              <ErrorMessage>{errors.password.message}</ErrorMessage>
            )}

            {/* Password Suggestion Feature */}
            <PasswordSuggestion>
              <SuggestionHeader>
                <SuggestionTitle>💡 Password Helper</SuggestionTitle>
                <SuggestionActions>
                  <SuggestionButton onClick={handleGeneratePassword}>
                    <FiRefreshCw />
                    Generate
                  </SuggestionButton>
                </SuggestionActions>
              </SuggestionHeader>

              {showSuggestion && suggestedPassword && (
                <>
                  <SuggestedPassword>{suggestedPassword}</SuggestedPassword>
                  <PasswordStrength>
                    {[1, 2, 3, 4].map((bar) => (
                      <StrengthBar
                        key={bar}
                        className={
                          bar <= getPasswordStrength(suggestedPassword).bars
                            ? getPasswordStrength(suggestedPassword).class
                            : ""
                        }
                      />
                    ))}
                  </PasswordStrength>
                  <StrengthText>
                    Strength: {getPasswordStrength(suggestedPassword).label}
                  </StrengthText>
                  <SuggestionActions>
                    <SuggestionButton onClick={handleUseSuggestedPassword}>
                      Use This Password
                    </SuggestionButton>
                    <SuggestionButton onClick={handleCopyPassword}>
                      <FiCopy />
                      Copy
                    </SuggestionButton>
                  </SuggestionActions>
                </>
              )}
            </PasswordSuggestion>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <InputContainer>
              <InputIcon>
                <FiLock />
              </InputIcon>
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your new password"
                className={errors.confirmPassword ? "error" : ""}
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === password || "Passwords do not match",
                })}
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </PasswordToggle>
            </InputContainer>
            {errors.confirmPassword && (
              <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>
            )}
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <SubmitButton type="submit" disabled={loading}>
            {loading ? (
              <>
                <div className="loading-spinner" />
                Resetting Password...
              </>
            ) : (
              "Reset Password"
            )}
          </SubmitButton>
        </Form>

        <BackToLogin>
          <p>Remember your password?</p>
          <Link to="/login">
            <FiArrowLeft />
            Back to Sign In
          </Link>
        </BackToLogin>
      </ResetPasswordCard>
    </ResetPasswordContainer>
  );
};

export default ResetPassword;
